import { createRouter, createWebHistory } from 'vue-router'
import { TokenType } from '@/stores/authStore'
import { TICKET_CHANNELS } from '@/types/tickets'
import { setupRouterGuards } from './guards'
import Dashboard from '@/views/Dashboard/Dashboard.vue'
import Login from '@/views/Login/Login.vue'
import SignOut from '@/views/SignOut/SignOut.vue'

declare module 'vue-router' {
  interface RouteMeta {
    /**
     * Specifies which token types are allowed to access this route.
     *
     * Use `null` within the array to represent unauthenticated users.
     *
     * Special values:
     * - `undefined` (default): Only `TokenType.UserToken` is allowed
     * - `'any'`: Fully open access (any token type or unauthenticated)
     * - `[]` (empty array): No access allowed (always denied)
     *
     * Array examples:
     * - `[null]`: Only unauthenticated users
     * - `[TokenType.UserToken]`: Only authenticated users with UserToken
     * - `[TokenType.PreAuthenticationToken]`: Only pre-auth tokens
     * - `[TokenType.UserToken, null]`: Authenticated users OR unauthenticated
     */
    allowedTokenTypes?: (TokenType | null)[] | 'any'
    /**
     * If true, the route will be displayed in fullscreen mode (no header)
     * - default: `false`
     */
    fullscreen?: boolean
    /**
     * If true, the route renders with a slimmed-down header that keeps the
     * logo, token switch, and settings trigger while hiding navigation, search,
     * and favorites. Used for partner onboarding flows where we still want the
     * user to be able to switch tokens or sign out.
     * - default: `false`
     */
    minimalHeader?: boolean
    /**
     * If true, the route's content container will use fluid width (full viewport width)
     * instead of being constrained by max-width breakpoints.
     * - default: `false`
     */
    fluid?: boolean
    /**
     * If true, the route is a valid navigation menu target and is offered in
     * the `RouteSelect` item-component dropdown used when editing
     * `navigation_menu` entries in the ui--structures admin view (ADA-4292).
     * This is the authoritative
     * list of menuable views — the backend only validates the name format
     * (regex), not that the route exists.
     * - default: `false`
     */
    menuTarget?: boolean
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      name: 'Dashboard',
      path: '/',
      component: Dashboard,
      meta: {
        menuTarget: true,
      },
    },
    {
      name: 'Login',
      path: '/login',
      component: Login,
      meta: {
        allowedTokenTypes: [null],
        fullscreen: true,
      },
    },
    {
      name: 'SignOut',
      path: '/sign-out',
      alias: ['/sign-out-callback', '/signout-callback'],
      component: SignOut,
      meta: {
        fullscreen: true,
      },
    },
    {
      name: 'Open',
      path: '/open',
      redirect: (to) => {
        if (typeof to.query.resource === 'string' && typeof to.query.id === 'string') {
          return {
            name: 'ItemDisplay',
            params: {
              resource: to.query.resource,
              resourceId: to.query.id,
            },
            query: to.query.oauth ? { oauth: to.query.oauth } : {},
          }
        } else {
          return { name: 'PageNotFound' }
        }
      },
    },

    {
      name: 'SelfRegistration',
      path: '/self-registration',
      component: async () => import('@/views/SelfRegistration/SelfRegistration.vue'),
      meta: {
        allowedTokenTypes: [TokenType.PreAuthenticationToken, null],
        fullscreen: true,
      },
    },
    {
      name: 'OnboardingCompleted',
      path: '/onboarding-completed',
      component: async () => import('@/views/OnboardingCompleted/OnboardingCompleted.vue'),
    },
    {
      name: 'CreditorOnboarding',
      path: '/creditor-onboarding',
      component: async () => import('@/views/CreditorOnboarding/CreditorOnboarding.vue'),
      meta: {
        minimalHeader: true,
      },
    },
    {
      name: 'CustomerOnboarding',
      path: '/onboarding/:onboardingId?',
      component: async () => import('@/views/CustomerOnboarding/CustomerOnboarding.vue'),
      meta: {
        allowedTokenTypes: [TokenType.PreAuthenticationToken, TokenType.UserToken],
        minimalHeader: true,
      },
    },
    {
      name: 'VismaNetRegistration',
      path: '/visma-net-registration',
      component: async () => import('@/views/VismaNetRegistration/VismaNetRegistration.vue'),
      meta: {
        fullscreen: true,
        allowedTokenTypes: [TokenType.PreAuthenticationToken],
      },
    },
    {
      name: 'VismaNetSettings',
      path: '/visma-net-settings/:mode(initial|update)/:creditorId?',
      component: async () => import('@/views/VismaNetSettings/VismaNetSettings.vue'),
    },

    {
      name: 'ItemSearch',
      path: '/item-search/:parent?/:resource',
      component: async () => import('@/views/ItemSearch/ItemSearch.vue'),
      meta: {
        menuTarget: true,
      },
    },
    {
      name: 'ItemDisplayForeignKeysList',
      path: '/item-display/:resource/:resourceId/foreign-key/:foreignResource',
      component: async () => import('@/views/ItemDisplayForeignKeysList/ItemDisplayForeignKeysList.vue'),
    },
    {
      name: 'ItemDisplay',
      path: '/item-display/:resource/:resourceId?',
      component: async () => import('@/views/ItemDisplay/ItemDisplay.vue'),
    },

    {
      name: 'CaseRegistration',
      path: '/case-registration',
      component: async () => import('@/views/CaseRegistration/CaseRegistration.vue'),
      meta: {
        menuTarget: true,
      },
    },
    {
      name: 'CaseRegistrationSummary',
      path: '/case-registration/summary/:caseRegistrationId',
      component: async () => import('@/views/CaseRegistrationSummary/CaseRegistrationSummary.vue'),
    },
    {
      name: 'CaseRegistrationFileUpload',
      path: '/case-registration/file-upload',
      component: async () => import('@/views/CaseRegistrationFileUpload/CaseRegistrationFileUpload.vue'),
    },
    {
      name: 'Tickets',
      path: `/tickets/:channel(${TICKET_CHANNELS.join('|')})`,
      component: async () => import('@/views/Tickets/Tickets.vue'),
      meta: {
        fluid: true,
        menuTarget: true,
      },
    },
    {
      name: 'Ticket',
      path: `/tickets/:channel(${TICKET_CHANNELS.join('|')})/:ticketId`,
      component: async () => import('@/views/Ticket/Ticket.vue'),
      meta: {
        fluid: true,
      },
    },

    {
      name: 'CreditorReports',
      path: '/creditor-reports',
      component: async () => import('@/views/CreditorReports/CreditorReports.vue'),
      meta: {
        menuTarget: true,
      },
    },
    {
      name: 'UserManagement',
      path: '/user-management',
      component: async () => import('@/views/UserManagement/UserManagement.vue'),
      meta: {
        menuTarget: true,
      },
    },
    {
      name: 'UserManagementAccount',
      path: '/user-management/account/:accountId',
      component: async () => import('@/views/UserManagementAccount/UserManagementAccount.vue'),
    },
    {
      name: 'UserManagementUser',
      path: '/user-management/account/:accountId/user/:userId',
      component: async () => import('@/views/UserManagementUser/UserManagementUser.vue'),
    },
    {
      name: 'UserManagementUserEdit',
      path: '/user-management/account/:accountId/user/:userId/edit',
      component: async () => import('@/views/UserManagementUserEdit/UserManagementUserEdit.vue'),
    },

    {
      name: 'VismaNet',
      path: '/visma-net',
      component: async () => import('@/views/VismaNet/VismaNet.vue'),
      meta: {
        menuTarget: true,
      },
    },

    {
      name: 'InexchangeLookup',
      path: '/inexchange-lookup',
      component: async () => import('@/views/InexchangeLookup/InexchangeLookup.vue'),
      meta: {
        menuTarget: true,
      },
    },

    {
      name: 'FinanceOverpayments',
      path: '/finance-overpayments',
      component: async () => import('@/views/FinanceOverpayments/FinanceOverpayments.vue'),
      meta: {
        menuTarget: true,
      },
    },
    {
      name: 'FinanceUnplaced',
      path: '/finance-unplaced',
      component: async () => import('@/views/FinanceUnplaced/FinanceUnplaced.vue'),
      meta: {
        menuTarget: true,
      },
    },
    {
      name: 'FinanceCredits',
      path: '/finance-credits',
      component: async () => import('@/views/FinanceCredits/FinanceCredits.vue'),
      meta: {
        menuTarget: true,
      },
    },

    {
      name: 'ReversalHandlings',
      path: '/reversal-handlings',
      component: async () => import('@/views/ReversalHandlings/ReversalHandlings.vue'),
      meta: {
        menuTarget: true,
      },
    },
    {
      name: 'ReversalHandling',
      path: '/reversal-handlings/:handlingId',
      component: async () => import('@/views/ReversalHandlings/ReversalHandling.vue'),
    },

    {
      name: 'DomainSelectedChanges',
      path: '/domain-selected-changes/:tab(debt_description|foreign_address)?',
      component: async () => import('@/views/DomainSelectedChanges/DomainSelectedChanges.vue'),
      meta: {
        menuTarget: true,
      },
    },

    {
      name: 'PageNotFound',
      path: '/:pathMatch(.*)*',
      component: async () => import('@/views/PageNotFound/PageNotFound.vue'),
      meta: {
        allowedTokenTypes: 'any',
      },
    },
  ],
})

setupRouterGuards(router)

export default router
